// M&A Phase 1 deal lens — the honesty map.
//
// Every gap here is NAMED. A fact the lens was not given never renders as a
// zero (charter §3.1: blocked renders as a gap card, never as a green tick or
// a zero), and the fixed data-room list states plainly what no pre-signing
// filing can answer.

import type { MaTargetPackField } from './ma-target-packs';
import type { SynergyCandidateRegister } from './ma-synergy-candidate-map';
import { PRODUCER_GAP_CLASS_CARD_TITLE } from './ask-helix/contracts/producer-gap-class';
import {
  MA_DEAL_LENS_DATA_ROOM_UNLOCKS,
  MA_DEAL_LENS_DEAL_FACT_KEYS,
  type BuildMaDealLensInput,
  type MaDealLensGap,
  type MaDealLensGapKind,
} from './ma-deal-lens.types';

export function buildMaDealLensGaps(
  input: BuildMaDealLensInput,
  register: SynergyCandidateRegister,
  standaloneOi: MaTargetPackField<number | null>,
  separationPendingLeverIds: readonly string[]
): readonly MaDealLensGap[] {
  return [
    ...packDataGaps(input),
    ...leverGaps(register),
    ...collisionGaps(register),
    ...separationReceiptGaps(separationPendingLeverIds),
    ...rejectedCommitmentGaps(register),
    ...missingFactGaps(input, standaloneOi),
    ...MA_DEAL_LENS_DATA_ROOM_UNLOCKS.map((unlock) => ({
      gapKind: 'data_room_only' as const,
      gapCode: unlock.gapCode,
      label: unlock.label,
      unlockedBy: unlock.unlockedBy,
      synergyLeverId: null,
    })),
  ];
}

function separationReceiptGaps(
  separationPendingLeverIds: readonly string[]
): readonly MaDealLensGap[] {
  return separationPendingLeverIds.map((synergyLeverId) => ({
    gapKind: 'separation_receipt' as const,
    gapCode: `separation_receipt:${synergyLeverId}`,
    label: PRODUCER_GAP_CLASS_CARD_TITLE.separation_receipt,
    unlockedBy: PRODUCER_GAP_CLASS_CARD_TITLE.separation_receipt,
    synergyLeverId,
  }));
}

function packDataGaps(input: BuildMaDealLensInput): readonly MaDealLensGap[] {
  return input.targetPack.dataGaps.map((gap, index) => ({
    gapKind: 'target_pack_data_gap' as const,
    gapCode: `${input.targetPack.packId}:data_gap:${index}`,
    label: gap,
    unlockedBy: 'Diligence or Day-1 source-system access',
    synergyLeverId: null,
  }));
}

function leverGaps(register: SynergyCandidateRegister): readonly MaDealLensGap[] {
  return register.entries.flatMap((entry) => {
    const gapKind: MaDealLensGapKind =
      entry.certaintyClass === 'blocked' ? 'blocked_lever' : 'lever_blocking_gap';
    return entry.promotionGapCodes.map((code) => ({
      gapKind,
      gapCode: code,
      label: `${entry.title} — ${code.replace(/_/g, ' ')}`,
      unlockedBy: entry.promotionProof ?? 'Approval act promoting the lever',
      synergyLeverId: entry.synergyLeverId,
    }));
  });
}

function collisionGaps(register: SynergyCandidateRegister): readonly MaDealLensGap[] {
  return register.footprintCollisions.map((collision) => ({
    gapKind: 'footprint_collision' as const,
    gapCode: `footprint_atom_collision:${collision.atomKey}`,
    label:
      `Footprint atom ${collision.atomKey} is claimed by both ${collision.firstLeverId} ` +
      `and ${collision.secondLeverId}; neither lever's dollars count until it is resolved`,
    unlockedBy: 'Deconflict the atom so exactly one lever draws it',
    synergyLeverId: collision.secondLeverId,
  }));
}

function rejectedCommitmentGaps(register: SynergyCandidateRegister): readonly MaDealLensGap[] {
  return register.rejectedCommitments.map((rejected) => ({
    gapKind: 'rejected_commitment' as const,
    gapCode: 'commitment_rejected',
    label: `${rejected.synergyLeverId}: ${rejected.errors.join('; ')}`,
    unlockedBy: 'Re-underwrite the commitment so it validates against ma_synergy_commitment_v1',
    synergyLeverId: rejected.synergyLeverId,
  }));
}

function missingFactGaps(
  input: BuildMaDealLensInput,
  standaloneOi: MaTargetPackField<number | null>
): readonly MaDealLensGap[] {
  const supplied = new Set(input.dealFacts.map((fact) => fact.key));
  const missingDealFacts = MA_DEAL_LENS_DEAL_FACT_KEYS.filter(
    (key) => !supplied.has(key)
  ).map((key) => ({
    gapKind: 'deal_fact_missing' as const,
    gapCode: `deal_fact_missing:${key}`,
    label: `Deal fact ${key} was not supplied to the lens and is not inferred`,
    unlockedBy: 'Supply the fact as an explicit input with its certainty class and source id',
    synergyLeverId: null,
  }));

  if (standaloneOi.value !== null) return missingDealFacts;

  return [
    ...missingDealFacts,
    {
      gapKind: 'target_fact_missing' as const,
      gapCode: 'target_fact_missing:operating_income_usd',
      label: 'Target standalone operating income is absent, so the EV bridge cannot close',
      unlockedBy: 'A filed or IR-disclosed target operating income for the stated fiscal period',
      synergyLeverId: null,
    },
  ];
}
